import { Card, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * The three non-loaded states every Google Maps-backed component falls back
 * to: no API key configured, the script failed to load, and still loading.
 * Extracted out of GeoBoundaryMap.jsx (originally jest-axe-tested there) so
 * BiliMap can reuse the identical, already-verified markup instead of a
 * third copy of the same three blocks (DiscoveryMap.jsx has the first copy).
 */

const displayTextPropType = PropTypes.string;
const classNamePropType = PropTypes.string;

/** No API key configured -- static placeholder card. */
export function NoKeyPlaceholder({ displayText, className }) {
    return (
        <Card
            className={`text-center bg-light ${className || ''}`}
            role="img"
            aria-label={`Map placeholder for ${displayText}`}
        >
            <Card.Body className="py-4">
                <span className="fs-2 d-block mb-2" aria-hidden="true">
                    📍
                </span>
                <span className="text-muted small">{displayText}</span>
            </Card.Body>
        </Card>
    );
}

NoKeyPlaceholder.propTypes = {
    displayText: displayTextPropType.isRequired,
    className: classNamePropType,
};

/** The Google Maps script failed to load. */
export function LoadErrorPlaceholder({ displayText, className }) {
    return (
        <Card
            className={`text-center bg-light ${className || ''}`}
            role="img"
            aria-label={`Map unavailable for ${displayText}`}
        >
            <Card.Body className="py-4">
                <span className="text-muted small">{displayText}</span>
            </Card.Body>
        </Card>
    );
}

LoadErrorPlaceholder.propTypes = {
    displayText: displayTextPropType.isRequired,
    className: classNamePropType,
};

/** The Google Maps script is still loading. */
export function LoadingSpinner({ displayText, className }) {
    return (
        <div
            className={`d-flex align-items-center justify-content-center bg-light rounded ${className || ''}`}
            aria-label={`Loading map for ${displayText}`}
            role="status"
        >
            <Spinner
                animation="border"
                variant="primary"
                size="sm"
                className="my-5"
            />
            <span className="visually-hidden">Loading map...</span>
        </div>
    );
}

LoadingSpinner.propTypes = {
    displayText: displayTextPropType.isRequired,
    className: classNamePropType,
};
